// KÖZÖS async entitás-választó (typeahead) — nagy listákhoz (fiók / kapcsolattartó / birtokló), ahol a sima
// select nem elég. Kontrollált: `value` az id (vagy null), `onChange(id|null)`. A keresés debounce-olt (300 ms),
// a hívó adja a `search(q)` függvényt (pl. a lista-endpoint `q`-paraméterével). A kiválasztott elem címkéjét a
// komponens jegyzi (a legutóbb választott opcióból), edit-előtöltésnél a hívó `initialOption`-nal adja meg.
// A Popover + Input + gomb-lista alapú (nincs Command-primitív a repóban) — fókusz/Escape a Popovertől.
import { matchesSearch } from '@atlas/shared';
import { CheckIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  id: string;
  label: string;
  /**
   * HALVÁNY második sor a listában (pl. egy hierarchia ÚTJA: „Advisory bér / Bérköltség"). A TRIGGERBE
   * SZÁNDÉKOSAN nem kerül bele — ott a rövid címke kell, hogy keskeny cellában se törjön szét —, a KERESÉS
   * viszont ráilleszt. Így egy ág nevére gépelve az alatta lévő összes elem előjön, az útjával együtt: ez
   * adja a kaszkádos (szűkítős) választó felfedező-hasznát egyetlen mezőből.
   */
  sublabel?: string | undefined;
}

export interface EntityComboboxProps {
  value: string | null;
  onChange: (id: string | null) => void;
  /**
   * Debounce-olt SZERVER-keresés — a hívó adja (pl. a lista-endpoint `q`-jával). Üres `q` → az első N elem.
   * PONTOSAN EGYET adj meg: `search` (szerver-oldali) VAGY `options` (helyi lista).
   */
  search?: ((q: string) => Promise<ComboboxOption[]>) | undefined;
  /**
   * HELYI opció-lista (már betöltött készlet, pl. a form `refs` végpontjából). Ilyenkor a szűrés
   * SZINKRON és debounce NÉLKÜL fut: egy már memóriában lévő listán a 300 ms késleltetés csak lassúságnak
   * érződne, hálózati hívás pedig nincs, amit védeni kellene. A szűrés kis-nagybetű- és ékezet-érzéketlen.
   */
  options?: readonly ComboboxOption[] | undefined;
  /** Edit-előtöltés: a jelenlegi érték címkéje (hogy ne kelljen külön lekérni). */
  initialOption?: ComboboxOption | null | undefined;
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** Engedélyezi a kiválasztás törlését (X). Alap: true. */
  clearable?: boolean | undefined;
  /**
   * A helyőrző kisbetűs — szűrő-sávban a „mind" állapot (a legördülő-szabály: a „mind" MINDIG kisbetűs, a
   * tulajdonnév-listán is). A kiválasztott név érintetlen marad. Ugyanaz, mint az `EntityMultiSelect`-é.
   */
  lowercasePlaceholder?: boolean | undefined;
  'aria-label'?: string | undefined;
  /**
   * SAJÁT nyitó-elem a mező helyett (pl. egy fejléc-chip) — a kereshető lista ugyanaz, csak a trigger más.
   * Ref-továbbító, `asChild`-képes elem kell (gomb). Ilyenkor nincs törlő-X: a nullázás a `nullOptionLabel`
   * sorával megy, és a popover nem a trigger szélességét veszi fel (egy chip ahhoz túl keskeny).
   */
  trigger?: ReactNode | undefined;
  /** A lista ELSŐ sora, ami `null`-ra állít (pl. „Nincs felelős") — a kereséstől függetlenül mindig látszik. */
  nullOptionLabel?: string | undefined;
}

/**
 * A HELYI lista szűrése — a közös `matchesSearch` (ékezet- és kis-nagybetű-érzéketlen). A `sublabel` IS
 * illeszkedési felület (l. ott): a hierarchia-útra gépelve az ág minden eleme előjön.
 */
function filterLocalOptions(options: readonly ComboboxOption[], query: string): ComboboxOption[] {
  if (query.trim() === '') return [...options];
  return options.filter(
    (o) =>
      matchesSearch(o.label, query) ||
      (o.sublabel !== undefined && matchesSearch(o.sublabel, query)),
  );
}

export function EntityCombobox({
  value,
  onChange,
  search,
  options,
  initialOption,
  placeholder,
  lowercasePlaceholder = false,
  disabled,
  clearable = true,
  'aria-label': ariaLabel,
  trigger,
  nullOptionLabel,
}: EntityComboboxProps): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ComboboxOption[]>([]);
  const [loading, setLoading] = useState(false);
  // A kiválasztott elem címkéje — a legutóbb választottból / az initialOption-ból.
  const [selectedLabel, setSelectedLabel] = useState<string | null>(initialOption?.label ?? null);
  // A kiválasztott elem ÚTJA a triggerben, halványan: külön SOR alatta eltolná a cellát (mért hiba a
  // kiadás-listán), a hovatartozás viszont enélkül nem derülne ki. Szűk mezőben az út csonkul el előbb,
  // mert a név után áll.
  const [selectedSublabel, setSelectedSublabel] = useState<string | null>(
    initialOption?.sublabel ?? null,
  );
  const reqId = useRef(0);

  // Ha a value/initialOption kívülről változik (pl. edit-reset), frissítjük a megjelenített címkét.
  useEffect(() => {
    if (value === null) {
      setSelectedLabel(null);
      setSelectedSublabel(null);
    } else if (initialOption?.id === value) {
      setSelectedLabel(initialOption.label);
      setSelectedSublabel(initialOption.sublabel ?? null);
    }
  }, [value, initialOption]);

  // HELYI lista: szinkron szűrés (nincs debounce, nincs betöltő-állapot).
  useEffect(() => {
    if (!open || options === undefined) return;
    setLoading(false);
    setResults(filterLocalOptions(options, query));
  }, [open, options, query]);

  // Debounce-olt SZERVER-keresés, csak nyitott állapotban.
  useEffect(() => {
    if (!open || search === undefined) return;
    const my = ++reqId.current;
    setLoading(true);
    const timer = setTimeout(() => {
      void search(query.trim())
        .then((opts) => {
          if (my === reqId.current) setResults(opts);
        })
        .catch(() => {
          if (my === reqId.current) setResults([]);
        })
        .finally(() => {
          if (my === reqId.current) setLoading(false);
        });
    }, 300);
    return () => clearTimeout(timer);
  }, [query, open, search]);

  // ZÁRÁSKOR a keresőszöveg ürül: különben az újranyitott lista a régi szűréssel nyílt, és úgy tűnt, a többi
  // elem eltűnt (kolléga-észrevétel). A választás is ezen az úton zár.
  const handleOpenChange = (next: boolean): void => {
    setOpen(next);
    if (!next) setQuery('');
  };

  const pick = (opt: ComboboxOption): void => {
    setSelectedLabel(opt.label);
    setSelectedSublabel(opt.sublabel ?? null);
    onChange(opt.id);
    handleOpenChange(false);
  };

  const pickNull = (): void => {
    setSelectedLabel(null);
    setSelectedSublabel(null);
    onChange(null);
    handleOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      {trigger !== undefined ? (
        <PopoverTrigger asChild disabled={disabled}>
          {trigger}
        </PopoverTrigger>
      ) : (
        /* A HORGONY a TELJES mező-sor (a törlés-X-szel együtt), nem csak a gomb — a legördülő így pontosan
          olyan széles lesz, mint amibe a felhasználó belekattint. A triggert szándékosan a gombon hagyjuk:
          az X-re kattintás töröl, nem nyit. */
        <PopoverAnchor asChild>
          <div className="flex items-center gap-1">
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={disabled}
                aria-label={ariaLabel}
                // `input-glass` → az Input-mezővel EGYSÉGES keret/felület + a közös halvány zöld focus-ring.
                className="input-glass min-w-0 flex-1 justify-between font-normal"
              >
                <span className="flex min-w-0 items-baseline gap-1.5">
                  <span
                    className={cn(
                      'truncate',
                      !selectedLabel && 'text-muted-foreground',
                      !selectedLabel && lowercasePlaceholder && 'lowercase',
                    )}
                  >
                    {selectedLabel ?? placeholder ?? t('common.select')}
                  </span>
                  {selectedLabel !== null &&
                    selectedSublabel !== null &&
                    selectedSublabel !== '' && (
                      <span className="text-muted-foreground truncate text-xs font-normal">
                        {selectedSublabel}
                      </span>
                    )}
                </span>
                <ChevronUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            {clearable && value !== null && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={t('common.clear')}
                onClick={() => {
                  setSelectedLabel(null);
                  onChange(null);
                }}
              >
                <XMarkIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        </PopoverAnchor>
      )}
      {/* NINCS PORTÁL (`portal={false}`) — a lista a trigger DOM-fáján belül renderel, és ez KÖVETELMÉNY:
          modális Sheet/Dialog belsejében a body-szintű portált a `react-remove-scroll` nem engedi görgetni
          (mért hiba: a kiadás-űrlapon a munkatárs-választó legördülője nem volt görgethető). A Radix popper
          `position: fixed`, tehát az ősök overflow-ja nem vágja el — FELTÉVE, hogy egyik ős sem csinál
          TARTALMAZÓ BLOKKOT egy transformmal. A `DialogContent` ezért igazodik flex-burkolóval, nem
          `translate-x-[-50%]`-kel (lásd `ui/dialog.tsx`); amíg az úgy marad, ez a popover sem vágódik.

          A SZÉLESSÉG a HORGONY szélessége (`--radix-popover-trigger-width`), nem egy fix `w-72`: a legördülő
          így pontosan a mező alá illeszkedik. A TÉRKÖZ pedig ugyanazzal a mechanizmussal készül, mint a
          `SelectContent`-é (`sideOffset=0` + `translate-y-1`) — két külön mechanizmus nem tudna pixelre
          egyezni, és a modálban ez látszott is. */}
      <PopoverContent
        align="start"
        sideOffset={0}
        portal={false}
        className={cn(
          'p-0 data-[side=bottom]:translate-y-1',
          trigger !== undefined ? 'w-72' : 'w-[var(--radix-popover-trigger-width)]',
        )}
      >
        {/* A kereső ALATT NINCS elválasztó vonal (user-döntés, 2026-08-25): a rendszerben egyetlen kereshető
            választóban sincs — a mező kerete önmagában elválaszt, a vonal csak zsúfolttá tette a popovert. */}
        <div className="p-2">
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // ENTER a keresőben: az ELSŐ találatot választja (gépelés → Enter), és SOHA nem küldi be a
            // környező űrlapot (a kereső a sheet `<form>`-jában ül — kolléga-észrevétel). Betöltés közben a
            // lista még a régi keresésé, ezért akkor nem választ.
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              const first = results[0];
              if (!loading && first !== undefined) pick(first);
            }}
            placeholder={t('common.searchPlaceholder')}
            aria-label={t('common.searchPlaceholder')}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {nullOptionLabel !== undefined && (
            <button
              type="button"
              onClick={pickNull}
              className="hover:bg-accent text-muted-foreground flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm"
            >
              <span className="truncate">{nullOptionLabel}</span>
              {value === null && <CheckIcon className="h-4 w-4 shrink-0" />}
            </button>
          )}
          {loading ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">{t('common.loading')}</p>
          ) : results.length === 0 ? (
            <p className="text-muted-foreground px-3 py-2 text-sm">{t('common.noResults')}</p>
          ) : (
            results.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => pick(opt)}
                className="hover:bg-accent flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{opt.label}</span>
                  {opt.sublabel !== undefined && opt.sublabel !== '' && (
                    <span className="text-muted-foreground block truncate text-xs">
                      {opt.sublabel}
                    </span>
                  )}
                </span>
                {opt.id === value && (
                  <CheckIcon className="text-muted-foreground h-4 w-4 shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
